import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  Signal,
} from '@angular/core';
import { PrimengButtonDirective } from '@web/shared/ui';
import { Guid, isNil, Nillable, once } from '@web/utils';
import { ButtonModule } from 'primeng/button';
import { MultimediaGridLayoutItemComponent } from '../../components/multimedia-grid-layout-item/multimedia-grid-layout-item.component';
import { AdminMultimediaDataSourceService } from '../../services/admin-multimedia-data-source.service';
import { AdminMultimediaCollectionItem } from '../../models/admin-multimedia-collection-item';
import { MultimediaTileComponent } from '../../../shared/components/multimedia-tile/multimedia-tile.component';
import { MultimediaGridMode } from '../../types/multimedia-grid-mode';
import { MultimediaGridService } from './multimedia-grid.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdminMultimediaGridChunkLazyLoad } from '../../models/admin-multimedia-grid-chunk-lazy-load';
import { AdminMultimediaGridFilter } from '../../models/admin-multimedia-grid-filter';
import { MultimediaGridLayoutComponent } from '../../components/multimedia-grid-layout/multimedia-grid-layout.component';
import { AdminMultimediaGridDefaultTemplateColumns } from '../../consts/admin-multimedia-grid-default-template-columns';

@Component({
  selector: 'app-multimedia-grid',
  imports: [
    PrimengButtonDirective,
    ButtonModule,
    MultimediaGridLayoutItemComponent,
    MultimediaTileComponent,
    MultimediaGridLayoutComponent,
  ],
  templateUrl: './multimedia-grid.component.html',
  styleUrl: './multimedia-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultimediaGridComponent implements OnInit {
  /* prettier-ignore */
  private readonly multimediaDataSource = inject(AdminMultimediaDataSourceService);
  private readonly multimediaGrid = inject(MultimediaGridService);

  /* prettier-ignore */
  public readonly templateColumns = input<Nillable<string>>(AdminMultimediaGridDefaultTemplateColumns);
  public readonly loadOnInit = input<boolean>(true);
  public readonly mode = input<MultimediaGridMode>('preview');
  public readonly multi = input<boolean>(false);
  public readonly itemClickEvent = output<Guid>();
  public readonly selectionChangeEvent = output<Guid[]>();

  protected readonly total = signal(0);
  protected readonly items = signal<AdminMultimediaCollectionItem[]>([]);
  protected readonly count = this.countSignal();
  protected readonly canLoadMore = this.canLoadMoreSignal();

  private readonly lastPage = this.lastPageSignal();
  private readonly defaultLimitSize = signal(90);
  private readonly chunkPart = signal(1);
  private readonly isEmpty = this.isEmptySignal();
  private readonly selectedIds = signal<Guid[]>([]);
  private readonly destroyRef = inject(DestroyRef);

  private filter?: Nillable<AdminMultimediaGridFilter>;

  public ngOnInit(): void {
    if (this.shouldLoadOnInit()) {
      this.loadChunk();
    }

    this.listenToLoad();
    this.listenToFilterChanges();
    this.listenToRefresh();
  }

  public handleLoadMoreClick(): void {
    this.incrementChunkPart();

    isNil(this.filter)
      ? this.loadChunk()
      : this.loadChunkWithFilter(this.filter, true);
  }

  public handleClickEvent(id: Guid): void {
    this.itemClickEvent.emit(id);

    if (this.isSelectMode()) {
      this.selectOrUnselect(id);
    }
  }

  public shouldShowInfo(item: AdminMultimediaCollectionItem): boolean {
    return !item.isImage;
  }

  protected isSelected(id: Guid): boolean {
    return this.selectedIds().includes(id);
  }

  private isEmptySignal(): Signal<boolean> {
    return computed(() => this.items().length === 0);
  }

  private listenToLoad(): void {
    this.multimediaGrid.load$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((filter) => {
        this.filter = filter;

        if (isNil(this.filter)) {
          this.reset();
          this.loadChunk(false);
        } else {
          this.loadChunkWithFilter(this.filter);
        }
      });
  }

  private listenToFilterChanges(): void {
    this.multimediaGrid.filter$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((filter) => {
        this.filter = filter;

        this.reset();
        this.loadChunkWithFilter(filter);
      });
  }

  private listenToRefresh(): void {
    this.multimediaGrid.refresh$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (isNil(this.filter)) {
          this.reset();
          this.loadChunk(false);
        } else {
          this.loadChunkWithFilter(this.filter);
        }
      });
  }

  private loadChunkWithFilter(
    filter: AdminMultimediaGridFilter,
    append = false,
  ): void {
    this.multimediaDataSource
      .fetchGridChunk(
        new AdminMultimediaGridChunkLazyLoad(
          this.chunkPart(),
          this.defaultLimitSize(),
          filter,
        ),
      )
      .pipe(once())
      .subscribe({
        next: (chunk) => {
          const { multimedia, total } = chunk;

          this.updateTotal(total);

          append
            ? this.appendItems(multimedia)
            : this.overrideItems(multimedia);
        },
      });
  }

  private loadChunk(append = true): void {
    this.multimediaDataSource
      .fetchGridChunk(
        new AdminMultimediaGridChunkLazyLoad(
          this.chunkPart(),
          this.defaultLimitSize(),
        ),
      )
      .pipe(once())
      .subscribe({
        next: (chunk) => {
          const { multimedia, total } = chunk;

          this.updateTotal(total);

          append
            ? this.appendItems(multimedia)
            : this.overrideItems(multimedia);
        },
      });
  }

  private appendItems(multimedia: AdminMultimediaCollectionItem[]): void {
    this.items.update((items) => [...items, ...multimedia]);
  }

  private overrideItems(multimedia: AdminMultimediaCollectionItem[]): void {
    this.items.set([...multimedia]);
  }

  private updateTotal(total: number): void {
    this.total.set(total);
  }

  private incrementChunkPart(): void {
    this.chunkPart.update((current) => current + 1);
  }

  private countSignal(): Signal<number> {
    return computed(() => this.items().length);
  }

  private lastPageSignal(): Signal<number> {
    return computed(() => Math.ceil(this.total() / this.defaultLimitSize()));
  }

  private canLoadMoreSignal(): Signal<boolean> {
    return computed(
      () => !(this.lastPage() === this.chunkPart()) && !this.isEmpty(),
    );
  }

  private selectOrUnselect(id: Guid): void {
    this.isSelected(id)
      ? this.unselect(id)
      : this.multi()
        ? this.selectMultiple(id)
        : this.selectSingle(id);

    this.notifySelectionChangeEvent();
  }

  private notifySelectionChangeEvent(): void {
    this.selectionChangeEvent.emit(this.selectedIds());
  }

  private selectSingle(id: Guid): void {
    this.selectedIds.set([id]);
  }

  private selectMultiple(id: Guid): void {
    this.selectedIds.update((ids) => [...ids, id]);
  }

  private unselect(id: Guid): void {
    this.selectedIds.update((items) =>
      items.filter((selectedId) => selectedId !== id),
    );
  }

  private isSelectMode(): boolean {
    return this.mode() === 'select';
  }

  private reset(): void {
    this.selectedIds.set([]);
    this.chunkPart.set(1);
  }

  private shouldLoadOnInit(): boolean {
    return this.loadOnInit();
  }
}
